/**
 * Phase F4 — Admin Provider Oversight
 * ------------------------------------
 * Lists every provider profile with metrics + assignment counts.
 *
 * Endpoints used:
 *   GET /api/admin/providers
 *   GET /api/admin/providers/{provider_id}    (drill-down: profile + metrics + requests + assignments)
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Activity, CheckCircle, X, Briefcase, Star,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function AdminProviderOversight() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [drawerData, setDrawerData] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/providers`, auth);
      setRows(res.data?.providers || []);
    } catch (e) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (uid) => {
    setDrawer(uid); setDrawerData(null);
    try {
      const res = await axios.get(`${API}/admin/providers/${uid}`, auth);
      setDrawerData(res.data || null);
    } catch (e) {
      toast.error('Failed to load provider');
    }
  };

  return (
    <div data-testid="admin-provider-oversight-page" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Users size={26} color={theme.colors.primary} />
          Provider Oversight
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Provider profiles, classifications, metrics, and assignments.
        </p>
      </div>

      {loading ? (
        <div data-testid="providers-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading providers…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="providers-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          No providers found.
        </div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: theme.colors.bg.secondary }}>
              <tr>
                {['Provider', 'Company', 'Classifications', 'Compliance', 'Active', 'Completed', 'Total Served', 'Rating', ''].map((h) => (
                  <th key={h} style={{
                    textAlign: 'left', padding: '10px 12px',
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                    color: theme.colors.text.muted, fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.user_id} data-testid={`provider-row-${p.user_id}`} style={{ borderTop: `1px solid ${theme.colors.borders}` }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: theme.colors.text.secondary }}>
                    {(p.user_id || '').slice(0, 8)}…
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: theme.colors.text.primary }}>
                    {p.company_name || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 11 }}>
                    {(p.provider_types || []).slice(0, 3).join(', ') || '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999,
                      background: `${theme.colors.primary}15`, color: theme.colors.primary,
                      fontSize: 11, fontWeight: 700,
                    }}>{p.compliance_status || '—'}</span>
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: theme.colors.status.success }}>
                    {p.metrics?.active_count ?? 0}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: theme.colors.accent.teal }}>
                    {p.metrics?.completed_count ?? 0}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: theme.colors.text.primary }}>
                    {p.metrics?.portfolios_served ?? 0}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {p.metrics?.average_rating != null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Star size={12} color={theme.colors.accent.salmon} />
                        {Number(p.metrics.average_rating).toFixed(1)}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      data-testid={`provider-inspect-${p.user_id}`}
                      onClick={() => openDrawer(p.user_id)}
                      style={{
                        padding: '6px 12px', borderRadius: theme.radius.sm, cursor: 'pointer',
                        border: 'none', background: theme.colors.primary, color: '#fff',
                        fontSize: 12, fontWeight: 700,
                      }}
                    >
                      Inspect
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawer && (
        <div data-testid="provider-drawer" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'flex-end', zIndex: 100,
        }} onClick={() => setDrawer(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 'min(620px, 100%)', height: '100%', background: theme.colors.bg.card,
            padding: 24, overflowY: 'auto', boxShadow: theme.colors.shadows.lg,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: theme.colors.text.primary }}>
                Provider Detail
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            {!drawerData ? <div>Loading…</div> : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Profile</div>
                  {[
                    ['Company', drawerData.profile?.company_name],
                    ['Legal Name', drawerData.profile?.legal_name],
                    ['Country', drawerData.profile?.country],
                    ['Bio', drawerData.profile?.biography],
                    ['Website', drawerData.profile?.website],
                    ['Classifications', (drawerData.profile?.provider_types || []).join(', ')],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${theme.colors.borders}`, fontSize: 13 }}>
                      <span style={{ color: theme.colors.text.muted }}>{k}</span>
                      <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{v || '—'}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Metrics</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {Object.entries(drawerData.metrics || {}).map(([k, v]) => (
                      <div key={k} style={{
                        padding: 8, background: theme.colors.bg.secondary,
                        borderRadius: theme.radius.sm, fontSize: 12,
                      }}>
                        <div style={{ color: theme.colors.text.muted, textTransform: 'uppercase', fontSize: 10 }}>{k}</div>
                        <div style={{ fontWeight: 700 }}>{String(v ?? '—')}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>
                    Assignments ({drawerData.assignments?.length || 0})
                  </div>
                  {(drawerData.assignments || []).slice(0, 10).map((a) => (
                    <div key={a.id} style={{
                      padding: 8, background: theme.colors.bg.secondary,
                      borderRadius: theme.radius.sm, fontSize: 12, marginBottom: 6,
                      display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span><Briefcase size={11} /> {a.portfolio_id}</span>
                      <span style={{ fontWeight: 700 }}>{a.status}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
